import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { store, RootState } from './redux/store';
import { setAuth, setUser } from './redux/authSlice';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import DashboardPage from './components/dashboard/DashboardPage';
import SettingsPage from './components/users/UsersPage';
import ScreensPage from './components/ds/screens/ScreensPage';
import ScreenDetailPage from './components/ds/screens/ScreenDetailPage';
import MediaPage from './components/ds/media/MediaPage';
import ImageEditorPage from './components/ds/media/ImageEditorPage';
import PlaylistsPage2 from './components/ds/playlists/PlaylistsPage2';
import SchedulesPage from './components/ds/schedules/SchedulesPage';
import ScheduleEditorPage from './components/ds/schedules/ScheduleEditorPage';
import ReportPage from './components/ds/reports/ReportPage';
import CommandQueuePage from './components/ds/commands/CommandQueuePage';
import ProfilePage from './components/profile/ProfilePage';
import AdminPage from './components/admin/AdminPage';
import ActivatePage from './components/ds/screens/ActivatePage';
import { DEFAULT_THEME_ID, getTheme, applyThemeCssVars } from './theme/themes';
import './components/i18n/i18n';
import './interceptors/axios';

const makeTheme = (accentColor: string, bgColor: string, paperColor: string, mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: { main: accentColor },
      background: {
        default: bgColor,
        paper:   paperColor,
      },
    },
    typography: {
      fontFamily: '"DM Sans", "Outfit", sans-serif',
      fontSize: 15,
      htmlFontSize: 15,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { fontSize: '15px' },
        },
      },
    },
  } as any);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useSelector((s: RootState) => s.auth.value);
  return auth ? <>{children}</> : <Navigate to="/login" />;
};

const AppInner: React.FC<{ themeId: string; onThemeChange: (id: string) => void }> = ({ themeId, onThemeChange }) => {
  const dispatch = useDispatch();
  const auth = useSelector((s: RootState) => s.auth.value);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!auth) { if (pingRef.current) clearInterval(pingRef.current); return; }
    pingRef.current = setInterval(() => { axios.patch('/company/portal/ping').catch(() => {}); }, 2 * 60 * 1000);
    return () => { if (pingRef.current) clearInterval(pingRef.current); };
  }, [auth]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 > Date.now()) {
        dispatch(setAuth(true));
        dispatch(setUser({ id: payload.id, login: payload.login, group: payload.group, isAdmin: payload.isAdmin }));
      } else {
        localStorage.removeItem('token');
      }
    } catch {
      localStorage.removeItem('token');
    }
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/*" element={
          <PrivateRoute>
            <Layout themeId={themeId} onThemeChange={onThemeChange}>
              <Routes>
                <Route path="/dashboard"     element={<DashboardPage />} />
                <Route path="/settings"      element={<SettingsPage />} />
                <Route path="/screens"       element={<ScreensPage />} />
                <Route path="/screens/:id"   element={<ScreenDetailPage />} />
                <Route path="/media"         element={<MediaPage />} />
                <Route path="/media/edit/:id" element={<ImageEditorPage />} />
                <Route path="/playlists"     element={<PlaylistsPage2 />} />
                <Route path="/playlists/:id" element={<PlaylistsPage2 />} />
                <Route path="/schedules"     element={<SchedulesPage />} />
                <Route path="/schedules/:id" element={<ScheduleEditorPage />} />
                <Route path="/reports"       element={<ReportPage />} />
                <Route path="/commands"      element={<CommandQueuePage />} />
                <Route path="/profile"       element={<ProfilePage themeId={themeId} onThemeChange={onThemeChange} />} />
                <Route path="*"             element={<Navigate to="/screens" />} />
              </Routes>
            </Layout>
          </PrivateRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
};

const App: React.FC = () => {
  const [themeId, setThemeId] = useState<string>(
    localStorage.getItem('pv-theme-id') || DEFAULT_THEME_ID
  );

  const currentTheme = getTheme(themeId);

  useEffect(() => {
    applyThemeCssVars(currentTheme);
    document.body.style.background = currentTheme.bodyGradient;
    document.body.style.minHeight = 'var(--app-h)';
  }, [currentTheme]);

  // The shell is a fixed-height box with inner scroll areas, so its height has to
  // match what is actually visible. 100dvh (see index.css) is close, but mobile
  // Safari also slides its bottom bar over the page; visualViewport reports the
  // real visible height, which keeps the end of every list above that bar.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;                                  // CSS dvh fallback covers it
    const apply = () => {
      // An open keyboard shrinks the visual viewport dramatically — leave the
      // layout alone in that case instead of collapsing the whole shell
      if (vv.height < window.innerHeight * 0.6) return;
      document.documentElement.style.setProperty('--app-h', Math.round(vv.height) + 'px');
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);

  const handleThemeChange = (id: string) => {
    setThemeId(id);
    localStorage.setItem('pv-theme-id', id);
  };

  const muiTheme = makeTheme(
    currentTheme.muiPrimary,
    currentTheme.muiBackground,
    currentTheme.muiPaper,
    currentTheme.mode,
  );

  return (
    <Provider store={store}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <AppInner themeId={themeId} onThemeChange={handleThemeChange} />
      </ThemeProvider>
    </Provider>
  );
};

export default App;
